import type { Map as ImmutableMap } from 'immutable';
import { DateTime } from 'luxon';
import type { SagaIterator } from 'redux-saga';
import { call, delay, put, race, select, take, takeEvery, takeLeading } from 'redux-saga/effects';

import { getLogger, httpClientInterface, requireEnvironmentConfig } from 'app-components/utils/core';
import { EPOCH_ISO } from 'app-components/utils/date';

import { accountsActions, accountsTypes } from '../accounts';
import { resourceSagaUtils, resourceStoreTypes } from '../core';
import { featureFlagsSelectors } from '../feature-flags';
import { transactionsActions, type transactionsTypes } from '../transactions';
import { mkResourceConfig as txnResourceConfig } from '../transactions/transactionsSagas';
import * as actions from './goalsActions';
import { getGoalById, getLastSyncDate, getResourcesById } from './goalsSelectors';
import { transformGoalToRequestData, transformQcsGoalsToGoals, transformResponseDataToGoal } from './goalsTransformers';
import { type Goal, type GoalProps, mkUpdateGoal } from './goalsTypes';

const log = getLogger('flux/goals/goalsSagas');
type GoalsSagaIterator = Generator<unknown, void, unknown>;

const { callFirst, callLast } = resourceSagaUtils.createGreedDebounce('goalsDumpUpdater');
export function* goalsDumpUpdater(resources: Goal[]): GoalsSagaIterator {
  const bumpGoals = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpGoals');
  if (bumpGoals) {
    yield callFirst(goalsDumpUpdaterFull, resources);
    yield callLast(goalsNudgeUpdater);
  }
}

function* goalsDumpUpdaterFull(resources: Goal[]): GoalsSagaIterator {
  yield put(actions.updateChangedGoals(transformQcsGoalsToGoals(resources), { context: 'BUMP_CHANGE_DUMP' }));
}

export function* goalsNudgeUpdater(): GoalsSagaIterator {
  const bumpGoals = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpGoals');
  if (bumpGoals) {
    yield put(actions.getGoals(undefined, { context: 'BUMP_CHANGE_NUDGE' }));
  }
}

// ====================================================
// ACTION WATCHERS to trigger SAGAS calls

type SuccessActionCreator =
  | typeof actions.getGoalsSuccess
  | typeof actions.updateGoalSuccess
  | typeof actions.deleteGoalSuccess;
type FailureActionCreator =
  | typeof actions.getGoalsFailure
  | typeof actions.updateGoalFailure
  | typeof actions.deleteGoalFailure;

const mkQcsSyncGoalsConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<GoalProps>();

const mkResourceConfig = (successAction: SuccessActionCreator, failureAction: FailureActionCreator) =>
  mkQcsSyncGoalsConfig({
    resourceName: 'goal',
    resourceBaseUrl: '/goals',
    getLastSyncDate,
    applyResourceFromCRUDResponse: true,

    transformServerResourceToClientResource: transformResponseDataToGoal,
    transformClientResourceToRequestData: transformGoalToRequestData,

    successAction,
    failureAction,
  });

export function* getGoalsActionWatcher(): GoalsSagaIterator {
  const config = mkResourceConfig(actions.getGoalsSuccess, actions.getGoalsFailure);
  yield resourceSagaUtils.takeAndDebounce(
    actions.getGoals.type,
    resourceSagaUtils.qcsSyncGetResources<GoalProps>,
    config,
  );
}
export function* getGoalsSuccessActionWatcher(): GoalsSagaIterator {
  yield takeEvery(
    actions.getGoalsSuccess.type,
    function* getGoalSuccess(action: ReturnType<typeof actions.getGoalsSuccess>): GoalsSagaIterator {
      try {
        const resources = action.payload?.resources ?? [];

        for (const goal of resources) {
          const { completedOn, id, isDeleted, savedSoFar = 0, targetAmount, type } = goal;

          if (isDeleted || !id || !type || targetAmount == null) continue;

          if (savedSoFar >= targetAmount && (!completedOn || completedOn === EPOCH_ISO)) {
            yield put(
              actions.updateGoal(
                mkUpdateGoal(type, {
                  id,
                  completedOn: DateTime.local().toISO() ?? undefined,
                }),
              ),
            );
          } else if (savedSoFar < targetAmount && completedOn && completedOn !== EPOCH_ISO) {
            yield put(
              actions.updateGoal(
                mkUpdateGoal(type, {
                  id,
                  completedOn: EPOCH_ISO,
                }),
              ),
            );
          }
        }
      } catch (error) {
        log.error('Error evaluating goal auto-completion', error);
      }
    },
  );
}

export function* updateGoalActionWatcher(): GoalsSagaIterator {
  const config = mkResourceConfig(actions.updateGoalSuccess, actions.updateGoalFailure);
  yield takeEvery(actions.updateGoal.type, resourceSagaUtils.qcsSyncUpdateResource<GoalProps>, config);
}

export function* deleteGoalSuccessActionWatcher(): GoalsSagaIterator {
  yield takeEvery(
    actions.deleteGoalSuccess.type,
    function* deleteGoalSuccess(action: ReturnType<typeof actions.deleteGoalSuccess>): GoalsSagaIterator {
      const goalAccountId = action.payload.accountId;
      if (goalAccountId) {
        yield put(
          accountsActions.applyAccountsChanges(
            [
              accountsTypes.mkUpdateAccount('GOAL', {
                // local optimistic cascade account deletion
                id: goalAccountId,
                isDeleted: true,
              }),
            ],
            { context: 'deleteGoalSuccess' },
          ),
        );
      }
      // let QCS have some time for resources to settle before refreshing account balances
      yield delay(6500);
      yield put(actions.refreshGoalsBalances());
    },
  );
}

export function* deleteGoalActionWatcher(): GoalsSagaIterator {
  const config = mkResourceConfig(actions.deleteGoalSuccess, actions.deleteGoalFailure);
  yield takeEvery(actions.deleteGoal.type, resourceSagaUtils.qcsSyncDeleteResource<GoalProps>, config);
}

type CreateGoalResponse = {
  data: {
    resource: Goal;
  };
};
type CreateGoalRequestPayload = ReturnType<typeof transformGoalToRequestData> & {
  initialContributions?: NonNullable<ReturnType<typeof actions.performCreateGoal>['payload']['contributions']>;
};

// eventually QCS will take the responsibility of creating the tag and the transactions, then this will need to be replaced with the default action
export function* performCreateGoalWatcher(): GoalsSagaIterator {
  yield takeEvery(
    actions.performCreateGoal.type,
    function* performCreateGoal({ payload, meta }: ReturnType<typeof actions.performCreateGoal>): GoalsSagaIterator {
      const { goal, contributions } = payload;
      const { resolve, reject, context } = meta ?? {};

      // if we want optimistic update, but I'm not convinced we do with Goals:
      // yield put(actions.createGoal(goal));

      try {
        const requestUrl = `${requireEnvironmentConfig().services_url}/goals`;
        const requestPayload: CreateGoalRequestPayload = {
          ...transformGoalToRequestData(goal),
          initialContributions: contributions,
        };

        const response = (yield call(
          httpClientInterface.send,
          'POST',
          requestUrl,
          requestPayload,
        )) as CreateGoalResponse;

        yield put(actions.createGoalSuccess(transformResponseDataToGoal(response.data.resource), { context }));
        if (resolve) {
          yield call(resolve, undefined);
        }
        // give QCS time before refreshing balance
        yield delay(5000);
        yield put(actions.refreshGoalsBalances());
      } catch (error) {
        log.log('Error creating goal, error=', error);

        yield put(actions.createGoalFailure({ payload: error }));
        if (reject) {
          yield call(reject, error);
        }
      }
    },
  );
}

type GoalRefreshTransactionAction =
  | ReturnType<typeof transactionsActions.createTransactionSuccess>
  | ReturnType<typeof transactionsActions.batchTransactionsSuccess>;

type GoalRefreshRaceResult = {
  createTransactionSuccess?: GoalRefreshTransactionAction;
  batchTransactionsSuccess?: GoalRefreshTransactionAction;
};

function* refreshGoalsTrigger(): GoalsSagaIterator {
  while (true) {
    const { createTransactionSuccess, batchTransactionsSuccess } = (yield race({
      createTransactionSuccess: take(transactionsActions.createTransactionSuccess.type),
      batchTransactionsSuccess: take(transactionsActions.batchTransactionsSuccess.type),
    })) as GoalRefreshRaceResult;
    const transactionAction = createTransactionSuccess || batchTransactionsSuccess;
    const context = transactionAction?.meta?.context;

    if (
      context === 'WithdrawFromGoal' ||
      context === 'ContributeToGoal' ||
      context === 'spending-plan-goals' ||
      context === 'Goals' ||
      context === 'AddGoal'
    ) {
      yield put(actions.getGoals(undefined, context ? { context } : undefined));
    }
  }
}

function* refreshGoalBalanceWatcher(): GoalsSagaIterator {
  yield takeEvery(
    actions.refreshGoalBalance.type,
    function* performBalanceRefresh({ payload }: ReturnType<typeof actions.refreshGoalBalance>): GoalsSagaIterator {
      const id = payload;
      try {
        const requestUrl = `${requireEnvironmentConfig().services_url}/goals/refresh-account-async/${id}`;
        yield call(httpClientInterface.send, 'PUT', requestUrl);
      } catch (e) {
        log.warn('Error refreshing Goal Balance for id:', id, '\nE:', e);
      }
    },
  );
}

function* refreshGoalsBalancesWatcher(): GoalsSagaIterator {
  yield takeEvery(actions.refreshGoalsBalances.type, function* performBalancesRefresh(): GoalsSagaIterator {
    try {
      const requestUrl = `${requireEnvironmentConfig().services_url}/goals/analysis-async`;
      yield call(httpClientInterface.send, 'POST', requestUrl);
    } catch (e) {
      log.warn('Error refreshing Goals Balances', e);
    }
  });
}

export function* getGoalsTransactionsActionWatcher() {
  yield takeLeading(transactionsActions.getGoalTransactions, function* getTransactions(action): SagaIterator {
    const goals: ImmutableMap<string, Goal> = yield select(getResourcesById);
    const accountIds = goals
      .map((goal) => goal.accountId)
      .filter((accountId) => Boolean(accountId))
      .toIndexedSeq()
      .join();
    if (!accountIds) {
      // QCS treats a missing/empty accountIds filter as unscoped and returns the user's entire
      // transaction history, so never issue this request without account ids.
      return;
    }
    const resourceConfig = txnResourceConfig({
      successAction: transactionsActions.getGoalTransactionsSuccess,
      failureAction: transactionsActions.getGoalTransactionsFailure,
      ...{ axiosConfig: { params: { accountIds } } },
      ...{ getLastSyncDate: () => null }, // setup single chunk load
    });
    yield call(resourceSagaUtils.qcsSyncGetResources<transactionsTypes.TransactionProps>, resourceConfig, action);
  });
}

export function* getGoalsTransactionsForAccountActionWatcher() {
  yield takeLeading(transactionsActions.getGoalTransactionsForGoal, function* getTransactions(action): SagaIterator {
    const goalId = action.payload;
    const goal: Goal = yield select(getGoalById, goalId);
    if (goal?.accountId) {
      // only GET if goal exists with a linked account (maybe user back-navigated after deleting a
      // goal; without an accountId the request would return unscoped transactions)
      const resourceConfig = txnResourceConfig({
        successAction: transactionsActions.getGoalTransactionsSuccess,
        failureAction: transactionsActions.getGoalTransactionsFailure,
        ...{ axiosConfig: { params: { accountIds: goal.accountId } } },
        ...{ getLastSyncDate: () => null }, // setup single chunk load
      });
      yield call(resourceSagaUtils.qcsSyncGetResources<transactionsTypes.TransactionProps>, resourceConfig, action);
    }
  });
}

// ====================================================
// EXPORTS

export const goalsSagas = [
  getGoalsActionWatcher,
  getGoalsSuccessActionWatcher,
  updateGoalActionWatcher,
  deleteGoalActionWatcher,
  deleteGoalSuccessActionWatcher,
  refreshGoalsTrigger,
  performCreateGoalWatcher,
  refreshGoalBalanceWatcher,
  refreshGoalsBalancesWatcher,
  getGoalsTransactionsActionWatcher,
  getGoalsTransactionsForAccountActionWatcher,
];

export default goalsSagas;
